import { useState, useContext } from 'react';
import {
  IconChevronDown,
  IconGripVertical,
  IconPencil,
  IconPlus,
  IconTrash,
  IconArrowsShuffle,
  IconFolderPlus,
} from '@tabler/icons-react';
import { DraggableAttributes } from '@dnd-kit/core';
import { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import { LangContext } from '@/context/LangProvider';
import { Input } from '@/components/ui/input';
import Switch from '@/components/switch/Switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { InterviewQuestions } from '@/types';
import { getGroupContentType } from '@/utils/questionGroupHelpers';
import { GroupContentType } from '@/enums';

interface GroupHeaderProps {
  group: InterviewQuestions;
  isOpen: boolean;
  isSubgroup?: boolean;
  isDisabled?: boolean;
  isAddingQuestionsDisabled?: boolean;
  isCreatingSubgroupDisabled?: boolean;
  hasChildrenError?: boolean;
  childrenErrorMessage?: string;
  onToggle: () => void;
  onNameChange: (name: string) => void;
  onRandomizeChange: (randomize: boolean) => void;
  onAddQuestionClick: () => void;
  onAddSubgroupClick?: () => void;
  onDeleteClick: () => void;
  dragHandleProps?: DraggableAttributes;
  dragListeners?: SyntheticListenerMap;
}

export function GroupHeader({
  group,
  isOpen,
  isSubgroup = false,
  isDisabled = false,
  isAddingQuestionsDisabled = false,
  isCreatingSubgroupDisabled = false,
  hasChildrenError = false,
  childrenErrorMessage,
  onToggle,
  onNameChange,
  onRandomizeChange,
  onAddQuestionClick,
  onAddSubgroupClick,
  onDeleteClick,
  dragHandleProps,
  dragListeners,
}: GroupHeaderProps) {
  const { lang } = useContext(LangContext);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState(group.name);

  const contentType = getGroupContentType(group);

  const getBadgeText = () => {
    if (contentType === GroupContentType.GROUPS) {
      return lang.get('msg.groups');
    }
    if (contentType === GroupContentType.QUESTIONS) {
      return lang.get('msg.questions');
    }
    return '';
  };

  const badgeText = getBadgeText();

  const handleNameClick = () => {
    if (!isDisabled) {
      setIsEditingName(true);
    }
  };

  const handleNameBlur = () => {
    setIsEditingName(false);
    if (editedName.trim() !== '' && editedName !== group.name) {
      onNameChange(editedName.trim());
    } else {
      setEditedName(group.name);
    }
  };

  const handleNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleNameBlur();
    } else if (e.key === 'Escape') {
      setEditedName(group.name);
      setIsEditingName(false);
    }
  };

  return (
    <div className="flex flex-col w-full">
      <div
        className={cn(
          'flex items-center justify-between gap-4 flex-wrap w-full p-4 bg-white border-b',
          isSubgroup && 'bg-gray-50',
          hasChildrenError && 'bg-red-50',
        )}
      >
        {/* Left side: Drag handle, Chevron, Name, Badge */}
        <div className="flex items-center gap-3 flex-1 min-w-[200px]">
          {/* Drag handle */}
          {!isDisabled && dragHandleProps && dragListeners && (
            <span {...dragHandleProps} {...dragListeners} className="cursor-grab">
              <IconGripVertical size={20} className="text-gray-400" />
            </span>
          )}

          {/* Chevron for collapse/expand */}
          <button type="button" onClick={onToggle} className="flex-shrink-0">
            <IconChevronDown
              size={20}
              className={cn(
                'transition-transform duration-200 text-gray-600',
                isOpen && 'rotate-180',
              )}
            />
          </button>

          {/* Group name - editable */}
          {isEditingName ? (
            <Input
              value={editedName}
              onChange={(e) => setEditedName(e.target.value)}
              onBlur={handleNameBlur}
              onKeyDown={handleNameKeyDown}
              autoFocus
              className="h-8 max-w-xs"
              disabled={isDisabled}
            />
          ) : (
            <button
              type="button"
              onClick={handleNameClick}
              disabled={isDisabled}
              className={cn(
                'flex items-center gap-2 font-medium text-left truncate',
                isSubgroup ? 'text-base' : 'text-lg',
                !isDisabled && 'hover:text-primary cursor-pointer',
              )}
              style={{ color: isSubgroup ? '#064e3b' : '#065f46' }}
            >
              <span className="truncate">{group.name}</span>
              {!isDisabled && <IconPencil size={16} className="flex-shrink-0 text-gray-400" />}
            </button>
          )}

          {/* Badge showing content type */}
          {badgeText && (
            <span
              className="px-2 py-1 text-xs font-medium rounded-full flex-shrink-0"
              style={{
                backgroundColor: contentType === GroupContentType.GROUPS ? '#fce7f3' : '#dcfce7',
                color: contentType === GroupContentType.GROUPS ? '#be185d' : '#166534',
              }}
            >
              {badgeText}
            </span>
          )}
        </div>

        {/* Right side: Randomize toggle, Add buttons, Delete button */}
        <TooltipProvider>
          <div className="flex items-center gap-3 flex-shrink-0 justify-end w-full sm:w-auto">
            {/* Randomize toggle */}
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-2">
                  <IconArrowsShuffle size={18} className="text-gray-500" />
                  <Switch
                    value={group.randomize}
                    func={() => onRandomizeChange(!group.randomize)}
                    isDisabled={isDisabled}
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>
                  {contentType === GroupContentType.GROUPS
                    ? lang.get('msg.randomizeGroups')
                    : lang.get('msg.randomizeQuestions')}
                </p>
              </TooltipContent>
            </Tooltip>

            {/* Add Question button */}
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={onAddQuestionClick}
                  disabled={isDisabled || isAddingQuestionsDisabled}
                  className={cn(
                    'p-1.5 rounded hover:bg-gray-100 transition-colors',
                    (isDisabled || isAddingQuestionsDisabled) && 'opacity-50 cursor-not-allowed',
                  )}
                >
                  <IconPlus size={20} className="text-gray-600" />
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{lang.get('msg.addQuestion')}</p>
              </TooltipContent>
            </Tooltip>

            {/* Add Subgroup button - only show for non-subgroups */}
            {!isSubgroup && onAddSubgroupClick && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={onAddSubgroupClick}
                    disabled={isCreatingSubgroupDisabled}
                    className={cn(
                      'p-1.5 rounded hover:bg-gray-100 transition-colors',
                      isCreatingSubgroupDisabled && 'opacity-50 cursor-not-allowed',
                    )}
                  >
                    <IconFolderPlus size={20} className="text-gray-600" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{lang.get('msg.createSubgroup')}</p>
                </TooltipContent>
              </Tooltip>
            )}

            {/* Delete button */}
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={onDeleteClick}
                  disabled={isDisabled}
                  className={cn(
                    'p-1.5 rounded hover:bg-red-50 transition-colors',
                    isDisabled && 'opacity-50 cursor-not-allowed',
                  )}
                >
                  <IconTrash size={20} className="text-red-600" />
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{lang.get('msg.delete')}</p>
              </TooltipContent>
            </Tooltip>
          </div>
        </TooltipProvider>
      </div>

      {/* Error message - shown below header */}
      {hasChildrenError && childrenErrorMessage && (
        <div className="px-4 py-2 bg-red-50 border-t border-red-200">
          <p className="text-xs text-red-600 font-medium">{lang.get(childrenErrorMessage)}</p>
        </div>
      )}
    </div>
  );
}
